import { converter, formatHex, parse, toGamut, wcagContrast, type CuloriColor } from 'culori'
import type { ThemeContrast, ThemeTokens } from '../theme/presets'
import { executeRecipeProgram, type RuntimeDiagnostic } from './safeRecipeRuntime'

interface OklchColor extends CuloriColor { mode: 'oklch'; l: number; c: number; h: number }
export interface ContrastAuditItem { foreground: string; background: string; ratio: number; target: number; passes: boolean }
export interface CustomThemeResult {
  tokens: ThemeTokens
  editorInk: string
  palette: string[]
  diagnostics: RuntimeDiagnostic[]
  audit: ContrastAuditItem[]
}

export const DEFAULT_CUSTOM_RECIPE = `// Full-control semantic theme recipe.
// The returned token map is final: Superelectric Ink audits it, but does not
// silently change it. completeTheme() is optional; every token can be replaced.
function theme(ctx) {
  const { bg, ink, n, contrastTarget } = ctx;

  // Generate a golden-angle candidate pool. i is the palette index and
  // t runs from 0 to 1, so any indexed palette formula can be used here.
  const palette = Array.from({ length: n }, (_, i) => {
    const t = n === 1 ? 0 : i / (n - 1);
    return toSrgb(oklch(
      0.56 + 0.12 * Math.sin(t * Math.PI),
      0.10,
      wrap360(bg.h + i * PHI)
    ));
  });

  const raised = mix(bg, ink, 0.08);
  const accent = ensureContrast(palette[0], raised, contrastTarget);
  const secondary = ensureContrast(palette[1], ink, contrastTarget);

  return {
    editorInk: hex(ink),
    palette: palette.map(hex),
    tokens: {
      ...completeTheme({
        background: bg,
        ink,
        raised,
        accent,
        secondary,
        panel: mix(bg, palette[2], 0.18),
        contrastTarget
      }),

      // Override any semantic token here for absolute control:
      // "--accent-hover": hex(palette[5]),
      // "--panel-accent": hex(ensureContrast(palette[3], palette[2], 4.5))
    }
  };
}`

const toOklch = converter('oklch')
const toRgb = converter('rgb')
const gamutMap = toGamut('rgb')
function clamp(value: number, min = 0, max = 1) { return Math.max(min, Math.min(max, value)) }
function wrap360(hue: number) { return ((hue % 360) + 360) % 360 }

function asOklch(input: unknown, fallback = '#000000'): OklchColor {
  const parsed = typeof input === 'string' ? parse(input) : input as CuloriColor
  const converted = toOklch(parsed || parse(fallback)!)
  return { mode: 'oklch', l: converted?.l ?? 0, c: converted?.c ?? 0, h: wrap360(converted?.h ?? 0) }
}

function isInSrgb(color: unknown) {
  const rgb = toRgb(color as CuloriColor)
  if (!rgb) return false
  const epsilon = 0.000001
  return [rgb.r, rgb.g, rgb.b].every(channel => channel !== undefined && channel >= -epsilon && channel <= 1 + epsilon)
}
function mapped(color: unknown): CuloriColor { return gamutMap(asOklch(color)) || { mode: 'rgb', r: 0, g: 0, b: 0 } }
function mapToSrgb(color: unknown): OklchColor { return asOklch(mapped(color)) }
function hex(color: unknown): string {
  const candidate = asOklch(color)
  if (!isInSrgb(candidate)) throw new Error(`Cannot serialize out-of-sRGB color oklch(${candidate.l.toFixed(3)} ${candidate.c.toFixed(3)} ${candidate.h.toFixed(1)}). Use toSrgb() explicitly.`)
  return formatHex(candidate) || '#000000'
}
function safeHex(color: unknown): string { return formatHex(mapped(color)) || '#000000' }
function rgbChannels(color: unknown): string {
  const rgb = toRgb(mapped(color))!
  return [rgb.r, rgb.g, rgb.b].map(channel => Math.round(clamp(channel ?? 0) * 255)).join(', ')
}
function contrastRatio(a: unknown, b: unknown) { return wcagContrast(mapped(a), mapped(b)) }
function mix(aInput: unknown, bInput: unknown, amount: number): OklchColor {
  const a = asOklch(aInput); const b = asOklch(bInput); const t = clamp(Number(amount))
  const delta = ((b.h - a.h + 540) % 360) - 180
  return { mode: 'oklch', l: a.l + (b.l - a.l) * t, c: a.c + (b.c - a.c) * t, h: wrap360(a.h + delta * t) }
}
function ensureContrast(candidateInput: unknown, backgroundInput: unknown, target: number): OklchColor {
  const candidate = asOklch(candidateInput); const background = asOklch(backgroundInput); const guardedTarget = Number(target) + 0.08
  if (contrastRatio(candidate, background) >= guardedTarget && isInSrgb(candidate)) return candidate
  let best: OklchColor | null = null; let distance = Infinity
  for (let step = 0; step <= 200; step += 1) {
    const attempt = { ...candidate, l: step / 200 }
    if (isInSrgb(attempt) && contrastRatio(attempt, background) >= guardedTarget) {
      const nextDistance = Math.abs(attempt.l - candidate.l)
      if (nextDistance < distance) { best = attempt; distance = nextDistance }
    }
  }
  if (best) return best
  const black = asOklch('#000000'); const white = asOklch('#ffffff')
  return contrastRatio(white, background) >= contrastRatio(black, background) ? white : black
}
function mostReadable(background: unknown, dark: unknown, light: unknown): OklchColor {
  return contrastRatio(dark, background) >= contrastRatio(light, background) ? asOklch(dark) : asOklch(light)
}
function oklch(l: number, c: number, h: number): OklchColor {
  if (![l, c, h].every(Number.isFinite)) throw new Error('oklch() requires three finite numbers.')
  if (l < 0 || l > 1) throw new Error(`OKLCH lightness ${l} is outside the 0–1 range.`)
  if (c < 0 || c > 0.4) throw new Error(`OKLCH chroma ${c} is outside the allowed 0–0.4 range.`)
  return { mode: 'oklch', l, c, h: wrap360(h) }
}

function completeTheme(input: Record<string, unknown>): ThemeTokens {
  const background = mapToSrgb(input.background || '#123f3a'); const ink = mapToSrgb(input.ink || '#f0e3c4')
  const raised = mapToSrgb(input.raised || mix(background, ink, 0.08))
  const accent = mapToSrgb(input.accent || oklch(background.l < ink.l ? 0.7 : 0.45, 0.1, ink.h + 30))
  const secondary = mapToSrgb(input.secondary || oklch(background.l < ink.l ? 0.62 : 0.5, 0.08, accent.h + 137.507764))
  const panel = mapToSrgb(input.panel || mix(background, secondary, 0.12)); const black = asOklch('#111111'); const white = asOklch('#fafafa')
  const target = Number(input.contrastTarget) === 7 ? 7 : 4.5
  const accentContrast = ensureContrast(mostReadable(accent, black, white), accent, target); const secondaryContrast = ensureContrast(mostReadable(secondary, black, white), secondary, target)
  const panelText = ensureContrast(ink, panel, target); const textBright = ensureContrast(ink, raised, target)
  const textNormal = ensureContrast(mix(textBright, background, 0.12), raised, target); const textMuted = ensureContrast(mix(textBright, background, 0.34), raised, 3.5)
  const border = ensureContrast(mix(textBright, background, 0.58), raised, 3); const darkTheme = background.l < ink.l
  const overlayChannel = darkTheme ? '255, 255, 255' : '0, 0, 0'
  const error = ensureContrast(oklch(darkTheme ? 0.72 : 0.48, 0.16, 25), raised, 4.5)
  const success = ensureContrast(oklch(darkTheme ? 0.72 : 0.48, 0.14, 150), raised, 4.5)
  const warning = ensureContrast(oklch(darkTheme ? 0.76 : 0.48, 0.14, 85), raised, 4.5)
  const diffAdded = ensureContrast(oklch(darkTheme ? 0.84 : 0.48, 0.1, 185), raised, target)
  const diffRemoved = ensureContrast(oklch(darkTheme ? 0.74 : 0.52, 0.15, 28), raised, 3.5)
  return {
    '--accent': safeHex(accent), '--accent-rgb': rgbChannels(accent), '--accent-link': safeHex(accent), '--accent-contrast': safeHex(accentContrast),
    '--accent-light': safeHex(mix(accent, accentContrast, 0.28)), '--accent-hover': safeHex(mix(accent, accentContrast, 0.12)), '--accent-active': safeHex(mix(accent, background, 0.13)),
    '--secondary': safeHex(secondary), '--secondary-rgb': rgbChannels(secondary), '--secondary-contrast': safeHex(secondaryContrast),
    '--surface-bg': safeHex(background), '--surface-raised': safeHex(raised), '--surface-border': safeHex(border),
    '--panel-bg': safeHex(panel), '--panel-text': safeHex(panelText), '--panel-muted': safeHex(mix(panelText, panel, 0.34)), '--panel-border': safeHex(mix(panelText, panel, 0.56)),
    '--panel-hover-bg': safeHex(mix(panel, panelText, 0.1)), '--panel-control-bg': safeHex(mix(panel, panelText, 0.07)), '--panel-accent': safeHex(ensureContrast(accent, panel, target)),
    '--panel-accent-rgb': rgbChannels(ensureContrast(accent, panel, target)), '--panel-active-bg': safeHex(accent), '--panel-active-text': safeHex(accentContrast),
    '--control-bg': safeHex(raised), '--control-text': safeHex(textNormal), '--control-border': safeHex(border), '--color-scheme': darkTheme ? 'dark' : 'light',
    '--text-bright': safeHex(textBright), '--text-bright-rgb': rgbChannels(textBright), '--text-normal': safeHex(textNormal), '--text-muted': safeHex(textMuted),
    '--text-faint': safeHex(mix(textBright, background, 0.5)), '--divider': safeHex(mix(textBright, background, 0.58)),
    '--error': safeHex(error), '--error-light': safeHex(mix(error, textBright, 0.2)), '--error-rgb': rgbChannels(error), '--success': safeHex(success), '--success-rgb': rgbChannels(success),
    '--warning': safeHex(warning), '--warning-light': safeHex(mix(warning, textBright, 0.22)), '--warning-rgb': rgbChannels(warning),
    '--diff-added': safeHex(diffAdded), '--diff-added-rgb': rgbChannels(diffAdded), '--diff-removed': safeHex(diffRemoved), '--diff-removed-rgb': rgbChannels(diffRemoved),
    '--button-hover-bg': `rgba(${overlayChannel}, ${darkTheme ? 0.14 : 0.07})`, '--button-active-bg': safeHex(textBright), '--button-active-text': safeHex(background),
    '--toolbar-bg': safeHex(secondary), '--toolbar-border': safeHex(border), '--toolbar-shadow': `0 10px 24px rgba(${rgbChannels(black)}, ${darkTheme ? 0.32 : 0.2})`,
    '--shadow-rgb': rgbChannels(black), '--surface-shadow': `0 15px 35px rgba(${rgbChannels(black)}, ${darkTheme ? 0.45 : 0.2})`,
    '--input-bg': `rgba(${overlayChannel}, ${darkTheme ? 0.06 : 0.04})`, '--white-rgb': '255, 255, 255', '--black-rgb': '0, 0, 0',
  }
}

export const CUSTOM_THEME_TOKEN_NAMES = Object.freeze(Object.keys(completeTheme({})))
const SOLID_COLOR_TOKENS = CUSTOM_THEME_TOKEN_NAMES.filter(key => !key.endsWith('-rgb') && !['--color-scheme', '--button-hover-bg', '--toolbar-shadow', '--surface-shadow', '--input-bg'].includes(key))
const AUDIT_PAIRS: Array<[string, string, number]> = [
  ['--text-bright', '--surface-raised', 4.5], ['--text-normal', '--surface-raised', 4.5], ['--text-muted', '--surface-raised', 3.5],
  ['--control-text', '--control-bg', 4.5], ['--accent-link', '--surface-raised', 4.5], ['--accent-contrast', '--accent', 4.5],
  ['--secondary-contrast', '--secondary', 4.5], ['--panel-text', '--panel-bg', 4.5], ['--panel-accent', '--panel-bg', 4.5], ['--button-active-text', '--button-active-bg', 4.5],
  ['--diff-added', '--surface-raised', 4.5], ['--diff-removed', '--surface-raised', 3.5],
]

export function runCustomTheme(source: string, backgroundInput: string, inkInput: string, generationBase: 'canvas-and-ink' | 'canvas-only', contrastMode: ThemeContrast): CustomThemeResult {
  const bg = asOklch(backgroundInput, '#f0e3c4'); const selectedInk = asOklch(inkInput, '#2b2115')
  const autoInk = ensureContrast(oklch(bg.l > 0.5 ? 0.14 : 0.94, 0.012, bg.h + 18), bg, 7); const ink = generationBase === 'canvas-only' ? autoInk : selectedInk
  const contrastTarget = contrastMode === 'high' ? 7 : 4.5
  const ctx = Object.freeze({ bg: Object.freeze(bg), ink: Object.freeze(ink), n: 8, contrastTarget, generationBase, contrastMode })
  const execution = executeRecipeProgram(source, {
    ctx, PHI: 137.507764, BLACK: Object.freeze(asOklch('#000000')), WHITE: Object.freeze(asOklch('#ffffff')),
    oklch, wrap360, mix, ensureContrast, mostReadable, contrastRatio, inSrgb: isInSrgb, toSrgb: mapToSrgb, hex, rgbChannels, completeTheme,
  })
  const diagnostics = [...execution.diagnostics]; const fallback = completeTheme({ background: bg, ink })
  if (diagnostics.some(item => item.severity === 'error')) return { tokens: fallback, editorInk: safeHex(ink), palette: [], diagnostics, audit: [] }
  const output = execution.value as any
  if (!output || typeof output !== 'object') diagnostics.push({ severity: 'error', code: 'contract', message: 'theme(ctx) must return an object.' })
  if (!output?.tokens || typeof output.tokens !== 'object') diagnostics.push({ severity: 'error', code: 'contract', message: 'theme(ctx) must return a tokens object.', path: 'tokens' })
  const tokens = output?.tokens && typeof output.tokens === 'object' ? output.tokens as ThemeTokens : fallback
  for (const key of CUSTOM_THEME_TOKEN_NAMES) if (typeof tokens[key] !== 'string' || !tokens[key].trim()) diagnostics.push({ severity: 'error', code: 'missing-token', message: `Required token ${key} is missing or is not a CSS string.`, path: `tokens.${key}` })
  for (const key of Object.keys(tokens)) if (!CUSTOM_THEME_TOKEN_NAMES.includes(key)) diagnostics.push({ severity: 'error', code: 'unknown-token', message: `Unknown theme token ${key}.`, path: `tokens.${key}` })
  for (const key of SOLID_COLOR_TOKENS) {
    if (typeof tokens[key] !== 'string') continue
    const color = parse(tokens[key])
    if (!color) diagnostics.push({ severity: 'error', code: 'color', message: `${key} is not a valid CSS color.`, path: `tokens.${key}` })
    else if (!isInSrgb(color)) diagnostics.push({ severity: 'error', code: 'gamut', message: `${key} falls outside standard sRGB.`, path: `tokens.${key}` })
  }
  const parsedEditorInk = typeof output?.editorInk === 'string' ? parse(output.editorInk) : undefined
  const editorInk = parsedEditorInk ? output.editorInk : safeHex(ink)
  if (!parsedEditorInk) diagnostics.push({ severity: 'error', code: 'color', message: 'editorInk must be a valid CSS color string.', path: 'editorInk' })
  else if (!isInSrgb(parsedEditorInk)) diagnostics.push({ severity: 'error', code: 'gamut', message: 'editorInk falls outside standard sRGB.', path: 'editorInk' })
  const palette: string[] = Array.isArray(output?.palette) ? output.palette.filter((value: unknown): value is string => typeof value === 'string') : []
  palette.forEach((value, index) => {
    const color = parse(value)
    if (!color) diagnostics.push({ severity: 'error', code: 'color', message: `palette[${index}] is not a valid CSS color.`, path: `palette.${index}` })
    else if (!isInSrgb(color)) diagnostics.push({ severity: 'error', code: 'gamut', message: `palette[${index}] falls outside standard sRGB.`, path: `palette.${index}` })
  })
  const audit = AUDIT_PAIRS.flatMap(([foreground, background, standardTarget]) => {
    const a = parse(tokens[foreground]); const b = parse(tokens[background]); if (!a || !b) return []
    const target = contrastMode === 'high' && standardTarget >= 4.5 ? 7 : standardTarget; const ratio = wcagContrast(a, b)
    return [{ foreground, background, target, ratio, passes: ratio >= target }]
  })
  for (const item of audit.filter(item => !item.passes)) diagnostics.push({ severity: 'warning', code: 'contrast', message: `${item.foreground} / ${item.background} is ${item.ratio.toFixed(2)}:1; target is ${item.target}:1.`, path: `tokens.${item.foreground}` })
  const hasError = diagnostics.some(item => item.severity === 'error')
  return { tokens: hasError ? fallback : tokens, editorInk: hasError ? safeHex(ink) : editorInk, palette, diagnostics, audit }
}

export type { RuntimeDiagnostic as CustomRecipeDiagnostic }
